import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Button } from '../components/Button';
import { Teams } from '../components/Teams';
import { Content } from '../components/Content';
import {
  Draft,
  DraftComplete,
  GuessDraftResponse,
  GuessModes,
  Team,
  TeamColors,
} from '../common.type';
import { checkTeam, checkTeams, newDraft } from '../helpers/draft';
import { Buffer } from 'buffer';
import { LayoutContext } from '../context/LayoutContext';
import { copy } from '../helpers/object';
import { Tries } from '../components/Tries';
import { decompress } from '../helpers/compress';
import { useIndexedDB } from 'react-indexed-db-hook';
import { DbLogs } from '../type';
import { Status } from '../components/Status';

const TeamsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3vmin;
  margin-top: 3vmin;
`;

const draftInit: Draft = newDraft();

export const Guess = () => {
  const { token } = useParams();
  const { add, getByIndex } = useIndexedDB('draftLogs');
  const navigate = useNavigate();
  const { brawlers } = useContext(LayoutContext);
  const [draft, setDraft] = useState<Draft>(draftInit);
  const [draftLocked, setDraftLocked] = useState<Draft>(draftInit);
  const [draftValid, setDraftValid] = useState<Draft>(draftInit);
  const [draftToken, setDraftToken] = useState<string>();
  const [tries, setTries] = useState<number>(0);
  const [lock, setLock] = useState<boolean>(false);
  const [win, setWin] = useState<boolean | undefined>(undefined);

  const disabled = useMemo(() => {
    return (
      Object.values(draft.blueTeam).findIndex((b) => b.id === null) !== -1 ||
      Object.values(draft.redTeam).findIndex((b) => b.id === null) !== -1
    );
  }, [draft.blueTeam, draft.redTeam]);

  const validate = useCallback(() => {
    setLock(true);

    const toRet: GuessDraftResponse = { win: false, blueTeam: {}, redTeam: {} };
    switch (draft.settings.mode) {
      case GuessModes.LAST:
        if (draft.settings.coin === TeamColors.BLUE) {
          if (draft.redTeam[3].id === draftValid.redTeam[3].id) {
            toRet.win = true;
          }
        }
        if (draft.settings.coin === TeamColors.RED) {
          if (draft.blueTeam[3].id === draftValid.blueTeam[3].id) {
            toRet.win = true;
          }
        }
        break;
      case GuessModes.TWO_LAST:
        if (
          draft.redTeam[3].id === draftValid.redTeam[3].id &&
          draft.blueTeam[3].id === draftValid.blueTeam[3].id
        ) {
          toRet.win = true;
        } else {
          if (
            draft.redTeam[3].ban &&
            draft.redTeam[3].starPower &&
            draft.redTeam[3].gadget &&
            draftValid.redTeam[3].id &&
            draft.redTeam[3].id === draftValid.redTeam[3].id
          ) {
            toRet.redTeam[3] = {
              id: draft.redTeam[3].id,
              ban: draft.redTeam[3].ban,
              starPower: draft.redTeam[3].starPower,
              gadget: draft.redTeam[3].gadget,
            };
          }
          if (
            draft.blueTeam[3].ban &&
            draft.redTeam[3].starPower &&
            draft.redTeam[3].gadget &&
            draftValid.blueTeam[3].id &&
            draft.blueTeam[3].id === draftValid.blueTeam[3].id
          ) {
            toRet.blueTeam[3] = {
              id: draft.blueTeam[3].id,
              ban: draft.blueTeam[3].ban,
              starPower: draft.redTeam[3].starPower,
              gadget: draft.redTeam[3].gadget,
            };
          }
        }
        break;
    }

    if (toRet.win) {
      setWin(true);
      setDraftLocked(copy(draftValid));
      setLock(false);
      add<DbLogs>({
        data: draftToken ?? '',
        status: { win: true, tries },
        dateCreation: new Date(),
      }).catch((err: unknown) => {
        console.error(err);
      });
      return;
    }
    setTries((v) => v + 1);

    if (tries + 1 >= draft.settings.tries) {
      setWin(false);
      setDraftLocked(copy(draftValid));
      setLock(false);
      add<DbLogs>({
        data: draftToken ?? '',
        status: { win: false, tries: tries + 1 },
        dateCreation: new Date(),
      }).catch((err: unknown) => {
        console.error(err);
      });
      return;
    }

    const newDraftLocked = {
      ...draftLocked,
      blueTeam: { ...draftLocked.blueTeam, ...toRet.blueTeam },
      redTeam: { ...draftLocked.redTeam, ...toRet.redTeam },
    };

    setDraftLocked(copy(newDraftLocked));
    setLock(false);
  }, [draft, draftValid]);

  useEffect(() => {
    if (!token || brawlers.length === 0) return;

    const loadToken = (): DraftComplete | undefined => {
      const compress = Buffer.from(token, 'base64').toString();
      setDraftToken(compress);
      const dValid: DraftComplete | undefined = decompress(compress);
      if (!dValid) {
        console.error('Parse error');
        navigate('/404');
        return;
      }

      const validBlue = checkTeam(dValid.blueTeam, brawlers);
      const validRed = checkTeam(dValid.redTeam, brawlers);
      const validTeams = checkTeams(dValid.blueTeam, dValid.redTeam);

      if (!validBlue || !validRed || !validTeams) {
        console.error('Not a valid team');
        navigate('/404');
        return;
      }

      setDraftValid(dValid);
      return dValid;
    };
    const initDraft = () => {
      const dValid = loadToken();
      if (!dValid) return;

      const dLocked: Draft = copy(dValid);
      switch (dLocked.settings.mode) {
        case GuessModes.LAST:
          if (dLocked.settings.coin === TeamColors.BLUE)
            dLocked.redTeam[3].id = null;
          if (dLocked.settings.coin === TeamColors.RED)
            dLocked.blueTeam[3].id = null;
          break;
        case GuessModes.TWO_LAST:
          dLocked.redTeam[3].id = null;
          dLocked.blueTeam[3].id = null;
          break;
      }

      setDraftLocked(dLocked);
      setDraft({
        ...draft,
        mapId: dValid.mapId,
        settings: { ...dValid.settings },
      });
    };

    getByIndex('data', token)
      .then((r: DbLogs | undefined) => {
        if (r) {
          const dValid = loadToken();
          if (!dValid) return;
          const dCopy: Draft = copy(dValid);
          setDraft(dCopy);
          setWin(r.status.win);
          setTries(r.status.tries);
          return;
        }
        initDraft();
      })
      .catch((err: unknown) => {
        console.error(err);
      });
  }, [token, brawlers]);

  if (!token) {
    return <Navigate to={'/404'} />;
  }

  return (
    <Content gap={0}>
      {win === true && (
        <Status $color="green" $size='L'>
          <FormattedMessage id={'victory'} />
        </Status>
      )}
      {win === false && (
        <Status $color="red" $size='L'>
          <FormattedMessage id="defeat" />
        </Status>
      )}
      <TeamsWrapper>
        <Teams
          blueTeam={draft.blueTeam}
          redTeam={draft.redTeam}
          settings={draft.settings}
          mapId={draft.mapId}
          updateTeams={(teams: { blueTeam: Team; redTeam: Team }) => {
            const cDraft = copy<Draft>(draft);
            setDraft({
              ...cDraft,
              blueTeam: { ...teams.blueTeam },
              redTeam: { ...teams.redTeam },
            });
          }}
          updateMapId={(_mapId: number) => {}}
          draftLocked={draftLocked}
        />
      </TeamsWrapper>
      <Tries current={tries} max={draft.settings.tries}></Tries>
      {win === undefined && (
        <Button onClick={validate} color="green" disabled={disabled || lock}>
          <FormattedMessage id={'validate'} />
        </Button>
      )}
    </Content>
  );
};
